import type { TFunction } from "i18next";
import type {
  AskNexusAnswerBlock,
  AskNexusCard,
  AskNexusCardProvenance,
  AskNexusComparisonTableCard,
  AskNexusCreativeExample,
  AskNexusCreativeExamplesCard,
  AskNexusDataCard,
  AskNexusFollowUpsCard,
  AskNexusKpiTilesCard,
  AskNexusLinkCard,
  AskNexusOpportunitiesCard,
  AskNexusOpportunitySuggestionCard,
  AskNexusQualityNote,
  AskNexusResolvedSource,
  AskNexusScriptCard,
  AskNexusTurn,
} from "@/features/ask-nexus/api/ask-nexus.api";
import { clarificationCopyLines } from "@/features/ask-nexus/lib/clarification";
import { formatBrandName } from "@/features/dashboard/lib/format";
import { platformDisplayName } from "@/features/dashboard/lib/my-brand-filter-options";

type KpiFormat = AskNexusKpiTilesCard["tiles"][number]["format"];
type ComparisonCell = AskNexusComparisonTableCard["rows"][number]["cells"][number];
type Opportunity = AskNexusOpportunitiesCard["items"][number];

/** Fraction digits by format. Percent values arrive already multiplied by 100, as the Nexus dashboard sends them. */
const FRACTION_DIGITS: Record<NonNullable<KpiFormat> | "plain", number> = {
  count: 0,
  percent: 1,
  ratio: 2,
  currency: 2,
  plain: 2,
};
/** The creative-example tool returns CTR as clicks divided by impressions. */
const CTR_TO_PERCENT = 100;

const TOOL_NAMES: Record<string, { key: string; fallback: string }> = {
  nexus_get_brand_performance: { key: "askNexus.cards.tools.brandPerformance", fallback: "Brand performance" },
  nexus_get_competitive_insights: { key: "askNexus.cards.tools.competitiveInsights", fallback: "Competitive insights" },
  nexus_list_creative_examples: { key: "askNexus.cards.tools.creativeExamples", fallback: "Ad examples" },
  nexus_get_recent_opportunities: { key: "askNexus.cards.tools.recentOpportunities", fallback: "Recent opportunities" },
};

const QUALITY_TEXT: Record<Exclude<AskNexusQualityNote["code"], "before_window" | "tool_note">, { key: string; fallback: string }> = {
  library_counts: { key: "askNexus.cards.quality.libraryCounts", fallback: "Counts of ads visible in public ad libraries." },
  no_competitor_spend: {
    key: "askNexus.cards.quality.noCompetitorSpend",
    fallback: "There is no spend, impressions or conversions for competitors, and coverage depends on what each brand runs publicly.",
  },
  share_is_volume: {
    key: "askNexus.cards.quality.shareIsVolume",
    fallback: "Share of assets is a share of creative volume, not of spend or of voice.",
  },
  owned_billed: {
    key: "askNexus.cards.quality.ownedBilled",
    fallback: "Measured from your connected ad accounts: spend and conversions are the billed figures.",
  },
  connected_only: {
    key: "askNexus.cards.quality.connectedOnly",
    fallback: "Covers only the connected platforms and accounts; the currency follows the organization's setting.",
  },
  kpi_per_org: { key: "askNexus.cards.quality.kpiPerOrg", fallback: "KPI names and formulas are specific to this organization." },
  stale: {
    key: "askNexus.cards.quality.stale",
    fallback: "The list changed while it was being read, so this shows the first page of the current list.",
  },
  library_activity_only: {
    key: "askNexus.cards.quality.libraryActivityOnly",
    fallback: "Competitor ads come from public ad libraries: Nexus has their activity, not their spend or results.",
  },
};

/** A value as the card shows it. No currency or unit is added that the result does not state. */
export function formatCardValue(value: number, format: KpiFormat, locale: string): string {
  const text = new Intl.NumberFormat(locale, { maximumFractionDigits: FRACTION_DIGITS[format ?? "plain"] }).format(value);
  return format === "percent" ? `${text}%` : text;
}

export function cardTitle(t: TFunction, card: AskNexusDataCard): string {
  if (card.card === "kpi_tiles") return t("askNexus.cards.kpiTitle", "Key results");
  if (card.card === "creative_examples") {
    return card.side === "my-brand"
      ? t("askNexus.cards.ownExamplesTitle", "Your ads")
      : t("askNexus.cards.competitorExamplesTitle", "Competitor ads");
  }
  if (card.card === "opportunities") return t("askNexus.cards.opportunitiesTitle", "Opportunities in Nexus");
  if (card.basis === "metrics") return t("askNexus.cards.metricsTitle", "Compared with competitors");
  return card.field === "days"
    ? t("askNexus.cards.topicsDaysTitle", "Topics, by days active")
    : t("askNexus.cards.topicsAssetsTitle", "Topics, by number of assets");
}

/** The data window and what the result was scoped to, when the tool reports them. */
export function cardPeriod(provenance: AskNexusCardProvenance): string | null {
  const parts: string[] = [];
  if (provenance.startDate && provenance.endDate) {
    const range = `${provenance.startDate} → ${provenance.endDate}`;
    parts.push(provenance.presetLabel ? `${range} (${provenance.presetLabel})` : range);
  }
  if (provenance.scope) parts.push(provenance.scope);
  return parts.length > 0 ? parts.join(" · ") : null;
}

/**
 * A column's brand, shown the way the dashboard shows it: the brand key Nexus returned, through
 * `formatBrandName`. Nexus stores no other display name for competitor brands, and the agent never
 * names them. Without a key the column keeps its role name.
 */
export function columnLabel(t: TFunction, column: AskNexusComparisonTableCard["columns"][number]): string {
  if (column.role === "benchmark") return t("askNexus.cards.roles.benchmark", "Benchmark");
  if (column.label) return formatBrandName(column.label);
  if (column.role === "mainCompetitor") return t("askNexus.cards.roles.mainCompetitor", "Main competitor");
  return t("askNexus.cards.roles.you", "You");
}

export function rowLabel(card: AskNexusComparisonTableCard, label: string): string {
  return card.basis === "metrics" ? humanize(label) : label;
}

export function cellText(t: TFunction, cell: ComparisonCell, locale: string): string {
  if (cell.note === "not_on_page") return t("askNexus.cards.cell.notOnPage", "Later page");
  if (cell.note === "not_returned" || cell.value === null) return t("askNexus.cards.cell.notReturned", "Not returned");
  if (cell.note === "no_data") return t("askNexus.cards.cell.noData", "0 (no data)");
  return formatCardValue(cell.value, null, locale);
}

/** The card's notes about what it does not state or did not get, in the order the card shows them. */
export function cardNotes(t: TFunction, card: AskNexusDataCard): string[] {
  const notes: string[] = [];
  if (card.card === "kpi_tiles" && card.tiles.some((tile) => tile.format === "currency")) {
    notes.push(t("askNexus.cards.noCurrency", "Amounts as Nexus reports them; the result doesn't state the currency."));
  }
  if (card.card === "comparison_table" && card.basis === "metrics") {
    notes.push(t("askNexus.cards.noUnits", "Values as Nexus reports them; the result doesn't state their units."));
  }
  if (card.card === "creative_examples" && card.side === "my-brand" && card.examples.some((example) => example.owned?.spend !== null)) {
    notes.push(t("askNexus.cards.noCurrency", "Amounts as Nexus reports them; the result doesn't state the currency."));
  }
  if (card.notReturned.length > 0) {
    notes.push(t("askNexus.cards.notReturned", "Not returned by the tool: {{names}}", { names: card.notReturned.join(", ") }));
  }
  return notes;
}

/**
 * The provenance lines under a card: where the values came from, then each limitation the tool states
 * about the data, then the benchmark and partial-page warnings. The tool's instructions to the agent
 * never reach these lines; the server drops them.
 */
export function provenanceLines(t: TFunction, provenance: AskNexusCardProvenance, locale: string): string[] {
  const origin = [
    t("askNexus.cards.from", "From {{tool}}", { tool: toolName(t, provenance.tool) }),
    sourceLabel(t, provenance.source),
    ...(provenance.pulledAt ? [t("askNexus.cards.pulled", "pulled {{when}}", { when: formatWhen(provenance.pulledAt, locale) })] : []),
  ].join(" · ");
  const lines = [origin, ...provenance.quality.map((note) => qualityText(t, note))];
  if (provenance.benchmarkBrands) {
    lines.push(
      t("askNexus.cards.benchmark", "Benchmark: one combined column for {{brands}}, not any one of them.", {
        brands: provenance.benchmarkBrands.map(formatBrandName).join(", "),
      }),
    );
  }
  if (provenance.isPartial) {
    lines.push(t("askNexus.cards.partial", "This is one page of a longer list, so some rows or brands may be missing."));
  }
  return lines;
}

/** An ad's brand: the organization's own name, or a competitor's key through `formatBrandName`. */
export function exampleBrand(t: TFunction, example: Pick<AskNexusCreativeExample, "brand" | "brandKind">): string {
  if (!example.brand) return t("askNexus.cards.unknownBrand", "Brand not returned");
  return example.brandKind === "competitor" ? formatBrandName(example.brand) : example.brand;
}

/** The figures the tool returned for one ad, labelled; nothing it did not return. */
export function exampleMetrics(t: TFunction, example: AskNexusCreativeExample, locale: string): Array<{ label: string; value: string }> {
  const plain = (value: number) => formatCardValue(value, null, locale);
  const metrics: Array<{ label: string; value: string | null }> = example.owned
    ? [
        { label: t("askNexus.cards.metrics.roas", "ROAS"), value: example.owned.roas === null ? null : plain(example.owned.roas) },
        { label: t("askNexus.cards.metrics.spend", "Spend"), value: example.owned.spend === null ? null : plain(example.owned.spend) },
        {
          label: t("askNexus.cards.metrics.ctr", "CTR"),
          value: example.owned.ctr === null ? null : formatCardValue(example.owned.ctr * CTR_TO_PERCENT, "percent", locale),
        },
        { label: t("askNexus.cards.metrics.ads", "Ads"), value: example.owned.ads === null ? null : plain(example.owned.ads) },
        {
          label: t("askNexus.cards.metrics.duplications", "Duplications"),
          value: example.owned.duplications === null ? null : plain(example.owned.duplications),
        },
      ]
    : [
        { label: t("askNexus.cards.metrics.daysLive", "Days live"), value: example.library?.days == null ? null : plain(example.library.days) },
        { label: t("askNexus.cards.metrics.ads", "Ads"), value: example.library?.ads == null ? null : plain(example.library.ads) },
        { label: t("askNexus.cards.metrics.assets", "Assets"), value: example.library?.assets == null ? null : plain(example.library.assets) },
        {
          label: t("askNexus.cards.metrics.daysPerAsset", "Days per asset"),
          value: example.library?.daysPerAsset == null ? null : plain(example.library.daysPerAsset),
        },
      ];
  return metrics.flatMap((metric) => (metric.value === null ? [] : [{ label: metric.label, value: metric.value }]));
}

export function opportunityDetails(t: TFunction, item: Opportunity, locale: string): string[] {
  return [
    ...(item.painPoint ? [t("askNexus.cards.opportunity.painPoint", "Pain point: {{text}}", { text: item.painPoint })] : []),
    ...(item.motivation ? [t("askNexus.cards.opportunity.motivation", "Motivation: {{text}}", { text: item.motivation })] : []),
    ...(item.drivenBy
      ? [
          item.drivenBy === "painpoint"
            ? t("askNexus.cards.opportunity.drivenByPainPoint", "Scored on its pain point")
            : t("askNexus.cards.opportunity.drivenByMotivation", "Scored on its motivation"),
        ]
      : []),
    t("askNexus.cards.opportunity.created", "Created {{when}}", { when: formatWhen(item.createdAt, locale) }),
  ];
}

/** What a script's or a suggestion's source is, as Nexus found it in the turn's results. */
export function resolvedSourceText(t: TFunction, source: AskNexusResolvedSource): string {
  if (source.kind === "pack") {
    return t("askNexus.cards.sources.pack", "{{tool}}, {{start}} → {{end}}", {
      tool: toolName(t, source.tool),
      start: source.startDate,
      end: source.endDate,
    });
  }
  if (source.kind === "creative_example") {
    return t("askNexus.cards.sources.example", "Ad example \"{{name}}\" ({{brand}})", { name: source.adName, brand: exampleBrand(t, source) });
  }
  return t("askNexus.cards.sources.opportunity", "Opportunity \"{{angle}}\" ({{set}})", { angle: source.angle, set: source.setName });
}

export function sceneTime(scene: AskNexusScriptCard["scenes"][number]): string {
  return `${scene.startSeconds}–${scene.endSeconds}s`;
}

/** One script on its own, everything it shows, for its Copy button. */
export function scriptCopyText(t: TFunction, card: AskNexusScriptCard): string {
  const meta = [
    t("askNexus.cards.script.angle", "Angle: {{text}}", { text: card.angle }),
    ...(card.persona ? [t("askNexus.cards.script.persona", "Persona: {{text}}", { text: card.persona })] : []),
    t("askNexus.cards.script.platform", "Platform: {{text}}", { text: card.platform }),
  ].join(" · ");
  return [
    t("askNexus.cards.script.label", "AI suggestion · not saved in Nexus"),
    `**${card.title}** · ${card.durationSeconds}s`,
    meta,
    "",
    ...card.scenes.flatMap((scene) => [
      `${sceneTime(scene)} · ${scene.label}`,
      t("askNexus.cards.script.visual", "Visual: {{text}}", { text: scene.visual }),
      ...(scene.voiceover ? [t("askNexus.cards.script.voiceover", "Voiceover: {{text}}", { text: scene.voiceover })] : []),
      ...(scene.onScreenText ? [t("askNexus.cards.script.onScreen", "On screen: {{text}}", { text: scene.onScreenText })] : []),
      "",
    ]),
    t("askNexus.cards.script.cta", "CTA: {{text}}", { text: card.cta }),
    ...sourcesLines(t, card.sources, card.unresolvedSources),
    ...(card.needsConfirmation.length > 0
      ? ["", t("askNexus.cards.script.checkBeforeUse", "Check before use:"), ...card.needsConfirmation.map((item) => `- ${item}`)]
      : []),
  ].join("\n");
}

/**
 * What Copy puts on the clipboard: the answer's text with each card written out
 * with its content and sources. A card that was not shown adds nothing, and no
 * card directive is ever part of it.
 */
export function answerCopyText(t: TFunction, turn: AskNexusTurn, locale: string): string {
  const blocks: AskNexusAnswerBlock[] = turn.blocks ?? (turn.answer ? [{ type: "markdown", text: turn.answer }] : []);
  return blocks
    .flatMap((block) => {
      if (block.type === "markdown") return [block.text];
      // Links and suggested questions are not content: Copy keeps the text around them.
      if (block.type === "card") return block.card.card === "link" || block.card.card === "follow_ups" ? [] : [cardText(t, block.card, locale)];
      return [];
    })
    .join("\n\n");
}

function cardText(t: TFunction, card: Exclude<AskNexusCard, AskNexusLinkCard | AskNexusFollowUpsCard>, locale: string): string {
  if (card.card === "script") return scriptCopyText(t, card);
  if (card.card === "opportunity_suggestion") return suggestionText(t, card);
  if (card.card === "clarification") {
    return [t("askNexus.cards.clarification.title", "Questions from Ask Nexus"), ...clarificationCopyLines(t, card)].join("\n");
  }
  if (card.card === "report_request") {
    const title = t("askNexus.cards.report.title", "{{report}} report · {{platform}}", { report: card.reportType.label, platform: platformDisplayName(t, card.platform) });
    return `**${title}**`;
  }
  const period = cardPeriod(card.provenance);
  return [
    period ? `**${cardTitle(t, card)}**, ${period}` : `**${cardTitle(t, card)}**`,
    "",
    ...cardBody(t, card, locale),
    "",
    ...cardNotes(t, card),
    ...provenanceLines(t, card.provenance, locale),
  ].join("\n");
}

function cardBody(t: TFunction, card: AskNexusDataCard, locale: string): string[] {
  if (card.card === "kpi_tiles") {
    return card.tiles.map(
      (tile) =>
        `- ${tile.label}: ${formatCardValue(tile.value, tile.format, locale)} (${t("askNexus.cards.previous", "Previous period: {{value}}", {
          value: formatCardValue(tile.previousValue, tile.format, locale),
        })})`,
    );
  }
  if (card.card === "comparison_table") {
    return [
      `| | ${card.columns.map((column) => columnLabel(t, column)).join(" | ")} |`,
      `|---|${card.columns.map(() => "---:").join("|")}|`,
      ...card.rows.map((row) => `| ${rowLabel(card, row.label)} | ${row.cells.map((cell) => cellText(t, cell, locale)).join(" | ")} |`),
    ];
  }
  if (card.card === "creative_examples") return card.examples.flatMap((example) => exampleText(t, example, locale));
  return card.items.flatMap((item) => [
    `- ${item.angle} (${t("askNexus.cards.opportunity.inNexus", "In Nexus · {{set}}", { set: item.setName })}, ${t("askNexus.cards.opportunity.rank", "rank {{rank}} in its set", { rank: item.rank })})`,
    `  ${t("askNexus.cards.opportunity.persona", "Persona: {{text}}", { text: item.personaTitle })} · ${item.type}`,
    ...opportunityDetails(t, item, locale).map((line) => `  ${line}`),
  ]);
}

function exampleText(t: TFunction, example: AskNexusCreativeExample, locale: string): string[] {
  const facts = [exampleBrand(t, example), ...(example.platform ? [example.platform] : []), ...(example.runDates ? [example.runDates] : [])];
  return [
    `- ${example.adName} (${facts.join(" · ")})`,
    ...(example.hook ? [`  ${t("askNexus.cards.example.hook", "Hook: {{text}}", { text: example.hook })}`] : []),
    ...(example.adCopy ? [`  ${t("askNexus.cards.example.copy", "Ad copy: {{text}}", { text: example.adCopy })}`] : []),
    ...exampleMetrics(t, example, locale).map((metric) => `  ${metric.label}: ${metric.value}`),
    ...(example.creativeUrl ? [`  ${t("askNexus.cards.example.originalLine", "Original: {{url}}", { url: example.creativeUrl })}`] : []),
  ];
}

function suggestionText(t: TFunction, card: AskNexusOpportunitySuggestionCard): string {
  return [
    t("askNexus.cards.suggestion.label", "Suggested by Ask Nexus · not saved"),
    `**${card.title}**`,
    card.description,
    ...(card.evidence.length > 0
      ? [
          "",
          t("askNexus.cards.suggestion.why", "Why (Ask Nexus's reading of this answer's data):"),
          ...card.evidence.map((item) => `- ${item.text}${evidenceSourceSuffix(t, item)}`),
        ]
      : []),
    t("askNexus.cards.suggestion.noScore", "Not scored: Nexus does not rank suggestions."),
  ].join("\n");
}

function evidenceSourceSuffix(t: TFunction, item: AskNexusOpportunitySuggestionCard["evidence"][number]): string {
  if (item.source) return ` (${resolvedSourceText(t, item.source)})`;
  if (item.isSourceMissing) return ` (${t("askNexus.cards.sources.missingOne", "its source isn't in this answer's data")})`;
  return "";
}

function sourcesLines(t: TFunction, sources: AskNexusResolvedSource[], unresolved: number): string[] {
  if (sources.length === 0 && unresolved === 0) return [];
  return [
    "",
    t("askNexus.cards.sources.title", "Sources:"),
    ...sources.map((source) => `- ${resolvedSourceText(t, source)}`),
    ...(unresolved > 0 ? [t("askNexus.cards.sources.unresolved", "{{count}} more aren't in this answer's data and aren't shown.", { count: unresolved })] : []),
  ];
}

export function qualityText(t: TFunction, note: AskNexusQualityNote): string {
  if (note.code === "tool_note") return note.text;
  if (note.code === "before_window") {
    return t("askNexus.cards.quality.beforeWindow", "{{brands}}: data only from before this period, so no figures here (not zero).", {
      brands: note.brands.map(formatBrandName).join(", "),
    });
  }
  return t(QUALITY_TEXT[note.code].key, QUALITY_TEXT[note.code].fallback);
}

function sourceLabel(t: TFunction, source: AskNexusCardProvenance["source"]): string {
  if (source === "owned_accounts") return t("askNexus.cards.sourceOwned", "your connected ad accounts");
  if (source === "public_ad_library") return t("askNexus.cards.sourceLibrary", "the public ad library");
  return t("askNexus.cards.sourceRecords", "opportunities saved in Nexus");
}

function formatWhen(iso: string, locale: string): string {
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? iso : date.toLocaleString(locale, { dateStyle: "medium", timeStyle: "short" });
}

function toolName(t: TFunction, tool: string): string {
  const name = TOOL_NAMES[tool];
  return name ? t(name.key, name.fallback) : tool;
}

/** "activeAds" → "Active ads": the metric's own name, made readable; nothing is added. */
function humanize(metric: string): string {
  const words = metric.replace(/[_-]+/g, " ").replace(/([a-z0-9])([A-Z])/g, "$1 $2").toLowerCase().trim();
  return words.charAt(0).toUpperCase() + words.slice(1);
}
